import {useMemo} from 'react';
import {useNavigate} from 'react-router';
import {Concept} from 'types';
import {isVerifiedLinkedWorkLink} from 'schemaDefinition';
import {useMetadataSaveState} from 'services/metadata/hooks/useMetadataSaveState';
import {assertWorkMetadataValue} from 'services/utils/functions';
import {Icon, IconButton, Layout, Spacer, Text} from 'components';
import {useLinkedValueSearchFieldContext} from 'sceneExtensions/extensions/linkedValueSearch/hooks/useLinkedValueSearchFieldContext';
import type {LinkedType} from '../types';
import type {EntityPreviewHeader} from './types';
import {LinkCatalogPostButtons} from './LinkCatalogPostButtons';

export const WorkPreviewHeader: EntityPreviewHeader = ({
  entityValue,
  linkingConfiguration,
}) => {
  const metadata = assertWorkMetadataValue(entityValue);
  const sourceConfig =
    useLinkedValueSearchFieldContext().sourceField?.fieldConfig;
  const navigate = useNavigate();
  const saving = useMetadataSaveState();

  const {title, linked, isCurrentSource} = useMemo((): {
    title: string;
    linked: LinkedType;
    isCurrentSource: boolean;
  } => {
    const currentValue = linkingConfiguration?.currentValue;
    return {
      title: metadata.work.preferredTitle?.[0] ?? '',
      linked:
        isVerifiedLinkedWorkLink(currentValue) &&
        currentValue.entityId === metadata.work.id
          ? 'verified'
          : 'none',
      isCurrentSource:
        sourceConfig?.entity === Concept.work &&
        sourceConfig?.sourceEntityId === metadata.work.id,
    };
  }, [
    linkingConfiguration?.currentValue,
    sourceConfig?.entity,
    sourceConfig?.sourceEntityId,
    metadata.work.id,
    metadata.work.preferredTitle,
  ]);

  return (
    <Layout
      paddingLeft={linked ? 1 : 2}
      paddingRight={1}
      alignItems="center"
      justifyContent="space-between"
      horizontal
      flex={1}>
      <Layout horizontal adjustCenter>
        {isCurrentSource ? (
          <>
            <Icon icon={'ArrowRight'} color={'primary'} />
            <Spacer width={1} />
          </>
        ) : linked === 'none' ? null : (
          <>
            <Icon icon={'CheckCircle'} />
            <Spacer width={1} />
          </>
        )}
        <Text variant="h2">{title}</Text>
        <IconButton
          icon={'Preview'}
          disabled={saving}
          onClick={() => navigate(`/metadata/${metadata.work.id}`)}
        />
      </Layout>
      {linkingConfiguration ? (
        <Layout horizontal adjustCenter>
          <LinkCatalogPostButtons
            title={title}
            linked={linked}
            linkingConfiguration={linkingConfiguration}
            entityValue={metadata}
          />
        </Layout>
      ) : null}
    </Layout>
  );
};
